import { useEffect, useRef, useState } from 'react';
import { ETIQUETTE_MAX, PASTILLE, pastille, type CandidatureDetail } from '@rh/shared';
import { appel } from '../api';
import { Pastille } from './Pastille';

/**
 * Pastille de potentiel posée à la main, sans ouvrir d'appel (demande du
 * 2026-09-19). La règle métier ne bouge pas : la pastille reste ce qu'elle a
 * toujours été (docs/BAREME.md § 1), et le prochain appel clôturé avec une
 * pastille la remplace. Elle se pose seulement plus tôt, sur lecture du CV.
 *
 * S'y ajoute l'étiquette libre écrite devant le chiffre (demande du
 * 2026-09-19) : « comptabilité », « vente », « caissière »… Elle dit le profil,
 * là où le chiffre dit ce qu'on en pense. Enregistrée seulement si elle change.
 *
 * Deux allures : `inline` sur la fiche (les six chiffres en clair) et
 * `pastille` sur la liste (un clic sur la pastille ouvre le choix).
 */
export function NoterPotentiel({
  candidatureId,
  potentiel,
  etiquette,
  nom,
  variante = 'inline',
  onChange,
}: {
  candidatureId: string;
  potentiel: number | null;
  etiquette: string | null;
  /** Nom du candidat, pour les libellés lus à voix haute. */
  nom: string;
  variante?: 'inline' | 'pastille';
  onChange: (fiche: CandidatureDetail) => void;
}) {
  const [ouvert, setOuvert] = useState(variante === 'inline');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const zone = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const [mot, setMot] = useState(etiquette ?? '');

  // L'étiquette venue du serveur reprend la main (relecture, envoi d'un autre champ).
  useEffect(() => setMot(etiquette ?? ''), [etiquette]);

  // Allure « pastille » : le choix se referme à Échap et au clic en dehors.
  useEffect(() => {
    if (variante === 'inline' || !ouvert) return;
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape') return;
      setOuvert(false);
      bouton.current?.focus();
    };
    const pointeur = (evenement: PointerEvent) => {
      if (!zone.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    document.addEventListener('keydown', clavier);
    document.addEventListener('pointerdown', pointeur);
    return () => {
      document.removeEventListener('keydown', clavier);
      document.removeEventListener('pointerdown', pointeur);
    };
  }, [ouvert, variante]);

  async function poser(valeur: number | null): Promise<void> {
    setErreur(null);
    setEnvoi(true);
    try {
      const fiche = await appel<CandidatureDetail>(`/candidatures/${candidatureId}/potentiel`, {
        method: 'PUT',
        body: JSON.stringify({ potentiel: valeur }),
      });
      onChange(fiche);
      if (variante === 'pastille') setOuvert(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Pastille non enregistrée');
    } finally {
      setEnvoi(false);
    }
  }

  async function poserEtiquette(): Promise<void> {
    const propre = mot.trim();
    if (propre === (etiquette ?? '')) return;
    setErreur(null);
    setEnvoi(true);
    try {
      onChange(
        await appel<CandidatureDetail>(`/candidatures/${candidatureId}/etiquette`, {
          method: 'PUT',
          body: JSON.stringify({ etiquette: propre || null }),
        }),
      );
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Étiquette non enregistrée');
    } finally {
      setEnvoi(false);
    }
  }

  const champEtiquette = (
    <label className="champ-etiquette">
      <span className="intitule">Profil</span>
      <input
        type="text"
        value={mot}
        maxLength={ETIQUETTE_MAX}
        placeholder="comptabilité, vente, caissière…"
        aria-label={`Profil de ${nom}`}
        disabled={envoi}
        onChange={(evenement) => setMot(evenement.target.value)}
        // Enregistrée en quittant le champ, ou à la touche Entrée.
        onBlur={() => void poserEtiquette()}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Enter') {
            evenement.preventDefault();
            evenement.currentTarget.blur();
          }
        }}
      />
    </label>
  );

  const choix = (
    <div className="choix-pastille" role="group" aria-label={`Potentiel de ${nom}`}>
      {PASTILLE.filter((niveau) => niveau.valeur !== null).map((niveau) => (
        <button
          key={niveau.valeur}
          type="button"
          className={niveau.valeur === potentiel ? 'actif' : ''}
          style={{ '--teinte-pastille': niveau.couleur } as React.CSSProperties}
          aria-pressed={niveau.valeur === potentiel}
          title={niveau.libelle}
          disabled={envoi}
          onClick={() => void poser(niveau.valeur)}
        >
          {niveau.valeur}
        </button>
      ))}
      {potentiel !== null && (
        <button type="button" className="lien" disabled={envoi} onClick={() => void poser(null)}>
          Effacer
        </button>
      )}
    </div>
  );

  if (variante === 'inline') {
    return (
      <div className="noter-potentiel" ref={zone}>
        {champEtiquette}
        <span className="intitule">Potentiel</span>
        {choix}
        {/* Ce que vaut le chiffre choisi, en clair : le survol n'existe pas sur téléphone. */}
        <span className="mot-pastille">{pastille(potentiel).libelle}</span>
        {erreur && <span className="signal-alerte">{erreur}</span>}
      </div>
    );
  }

  return (
    <div className="noter-potentiel compact" ref={zone}>
      <button
        ref={bouton}
        type="button"
        className="declencheur-pastille"
        aria-expanded={ouvert}
        aria-label={`Noter le potentiel de ${nom} — ${pastille(potentiel).libelle}`}
        onClick={() => setOuvert((v) => !v)}
      >
        <Pastille valeur={potentiel} libelle etiquette={etiquette} />
      </button>
      {ouvert && (
        <div className="panneau-pastille">
          {choix}
          {champEtiquette}
          {erreur && <p className="signal-alerte">{erreur}</p>}
        </div>
      )}
    </div>
  );
}
